import { AlertTriangle, CheckCircle } from "lucide-react";
import { InlineEditableText } from "../InlineEditableText";
import { InlineEditableSelect } from "../InlineEditableSelect";
import { OutcomeSection } from "./OutcomeSection";
import { IssueDetailsSection } from "./IssueDetailsSection";
import { EvidenceUsedSection } from "./EvidenceUsedSection";
import { AuditLogSection } from "./AuditLogSection";
import { ActivityLogSection } from "./ActivityLogSection";
import { type ActivityLog } from "./ActivityLogList";
import { type AuditSession } from "../../../../../models/audit/auditSession";
import { type CitationDocument } from "./types";

interface OverviewTabContentProps {
  columnName: string;
  isConclusive: boolean;
  outcome: string | null;
  displayStatus: string;
  attributeTestResult: AuditSession["sourceRows"][number]["attributeTestResults"][number];
  citationDocuments: CitationDocument[];
  activityLogs: ActivityLog[];
  logsLoading: boolean;
  sourceRowId?: string;
  onUpdate: (
    field: string,
    value: string | number | object | boolean | null,
  ) => Promise<void>;
  onDocumentClick: (documentEntry: CitationDocument) => void;
  disabled?: boolean;
}

export function OverviewTabContent({
  columnName,
  isConclusive,
  outcome,
  displayStatus,
  attributeTestResult,
  citationDocuments,
  activityLogs,
  logsLoading,
  sourceRowId,
  onUpdate,
  onDocumentClick,
  disabled = false,
}: OverviewTabContentProps) {
  return (
    <div className="p-2 h-[calc(100vh-160px)] overflow-y-auto max-w-3xl">
      <div className="space-y-4">
        <div className="border border-stone-200 overflow-hidden">
          <div className="px-4 py-2 bg-stone-100 border-b border-stone-200">
            <div className="font-medium">{columnName}</div>
          </div>

          <div className="p-4 space-y-4">
            <OutcomeSection
              isConclusive={isConclusive}
              outcome={outcome}
              attributeTestResult={attributeTestResult}
              onUpdate={onUpdate}
              disabled={disabled}
            />

            {!isConclusive && (
              <div>
                <div className="text-sm text-font-secondary font-mono mb-2">
                  Status
                </div>
                <InlineEditableSelect
                  disabled={disabled}
                  value=""
                  options={[
                    {
                      value: "pass",
                      label: "Pass",
                      description: "No issues found",
                    },
                    {
                      value: "fail",
                      label: "Fail",
                      description: "Issues found",
                    },
                    {
                      value: "not_applicable",
                      label: "Not Applicable",
                      description: "Test does not apply",
                    },
                  ]}
                  onSave={async (value) => {
                    await onUpdate("outcome", value);
                  }}
                  renderDisplay={() => {
                    if (displayStatus === "requires_attention") {
                      return (
                        <div className="bg-amber-100 text-amber-800 border border-amber-200 p-2 rounded-md inline-flex items-center gap-2">
                          <AlertTriangle className="h-6 w-6" />
                          Requires Attention
                        </div>
                      );
                    }
                    if (displayStatus === "issues_found") {
                      return (
                        <div className="bg-red-100 text-red-800 border border-red-200 p-2 rounded-md inline-flex items-center gap-2">
                          <AlertTriangle className="h-6 w-6" />
                          Issues Found
                        </div>
                      );
                    }
                    if (displayStatus === "na") {
                      return (
                        <div className="bg-gray-100 text-gray-800 border border-gray-200 p-2 rounded-md inline-flex items-center gap-2">
                          <AlertTriangle className="h-6 w-6" />
                          Not Applicable
                        </div>
                      );
                    }
                    if (displayStatus === "no_issues") {
                      return (
                        <div className="bg-green-100 text-green-800 border border-green-200 p-2 rounded-md inline-flex items-center gap-2">
                          <CheckCircle className="h-6 w-6" />
                          No issues
                        </div>
                      );
                    }
                    return (
                      <div className="text-sm text-font-secondary">
                        Unknown status
                      </div>
                    );
                  }}
                />
              </div>
            )}

            <IssueDetailsSection
              isConclusive={isConclusive}
              attributeTestResult={attributeTestResult}
              outcome={outcome}
              onUpdate={onUpdate}
              disabled={disabled}
            />
          </div>
        </div>

        {isConclusive &&
          attributeTestResult.isConclusive &&
          attributeTestResult.potentialMitigatingFactors && (
            <div>
              <div className="text-sm text-font-secondary font-mono mb-2">
                Potential Mitigating Factors
              </div>
              <InlineEditableText
                disabled={disabled}
                value={attributeTestResult.potentialMitigatingFactors || ""}
                onSave={async (value) => {
                  await onUpdate("potential_mitigating_factors", value);
                }}
                placeholder="Enter potential mitigating factors..."
              />
            </div>
          )}

        <EvidenceUsedSection
          citationDocuments={citationDocuments}
          onDocumentClick={onDocumentClick}
        />

        <AuditLogSection
          attributeTestColumnId={attributeTestResult.attributeTestColumnId}
          sourceRowId={sourceRowId}
        />

        <ActivityLogSection
          activityLogs={activityLogs}
          logsLoading={logsLoading}
        />
      </div>
    </div>
  );
}
